'use client';

import React, { useMemo } from 'react';
import Link from '@/components/Link';
import { Box } from '@dxp-feed/ui/Box';
import { Text } from '@dxp-feed/ui/Text';
import useMediaQuery from '@/utils/hooks/useMediaQuery';
import { addDataLayer } from '@/helpers/dataLayer';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@dxp-feed/ui';

interface FooterNavigationClientProps {
  horizontalList: any[];
  verticalList: any[];
}

function FooterNavigationClient({
  horizontalList,
  verticalList,
}: FooterNavigationClientProps) {
  const windowWidth = useMediaQuery();
  const isMobile = useMemo(
    () => windowWidth !== null && windowWidth <= 1023,
    [windowWidth],
  );

  const handleLinkClick = (label: string, link: string) => {
    if (typeof window !== 'undefined') {
      addDataLayer({
        event: 'nav_footer',
        click_text: label,
        click_url: link,
        page_title: document?.title,
      });
    }
  };

  const renderAccordionMenu = () => (
    <Accordion>
      <AccordionItem
        id="customer-service"
        className="border-grey350 border-b-[0.5px]"
      >
        <AccordionHeader
          id="customer-service"
          className="acccordian-header py-[16px] !px-0"
        >
          Customer Service
        </AccordionHeader>
        <AccordionPanel id="customer-service" className="p-0 accordian-body">
          <Text as="ul" className="p-0">
            {horizontalList?.map((item: any, i: number) => (
              <Text as="li" key={item?.label + i}>
                <Link
                  href={item?.link ?? '#'}
                  className="text-body"
                  target={item?.target}
                  prefetch={false}
                  onClick={() =>
                    handleLinkClick(item?.label, item?.link ?? '#')
                  }
                >
                  {item?.label}
                </Link>
              </Text>
            ))}
          </Text>
        </AccordionPanel>
      </AccordionItem>

      {verticalList?.map((menuitem: any, i: number) => (
        <AccordionItem
          id={menuitem?.heading + i}
          key={menuitem?.heading + i}
          className="border-grey350 border-b-[0.5px]"
        >
          <AccordionHeader
            id={menuitem?.heading + i}
            className="acccordian-header py-[16px] !px-0"
          >
            {menuitem?.heading}
          </AccordionHeader>
          <AccordionPanel
            id={menuitem?.heading + i}
            className="p-0 accordian-body"
          >
            <Text as="ul" className="p-0">
              {menuitem?.verticalLinks?.map((link: any, k: number) => (
                <Text as="li" key={link?.label + k}>
                  <Link
                    href={link?.link ?? '#'}
                    className="text-body"
                    target={link?.target}
                    prefetch={false}
                    onClick={() =>
                      handleLinkClick(link?.label, link?.link ?? '#')
                    }
                  >
                    {link?.label}
                  </Link>
                </Text>
              ))}
            </Text>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const renderDesktopMenu = () => (
    <>
      {verticalList?.map((item: any, i: number) => (
        <Box
          as="div"
          className="lg:col-span-1 hidden-tab"
          key={item?.heading + i}
        >
          <main>
            <Text
              as="p"
              className="text-brandBlack text-body font-semibold leading-normal mb-3"
            >
              {item?.heading}
            </Text>
          </main>
          <nav>
            <Text as="ul" className="[&>li>a:hover]:text-brandSecondary">
              {item?.verticalLinks?.map((innerItem: any, k: number) => (
                <Text as="li" key={innerItem?.label + k}>
                  <Link
                    href={innerItem?.link ?? '#'}
                    className="inline-block text-brandBlack text-body font-normal leading-normal mb-0 pt-1 pb-1 hover:underline"
                    target={innerItem?.target}
                    aria-label={innerItem?.label}
                    prefetch={false}
                    onClick={() =>
                      handleLinkClick(innerItem?.label, innerItem?.link ?? '#')
                    }
                  >
                    {innerItem?.label}
                  </Link>
                </Text>
              ))}
            </Text>
          </nav>
        </Box>
      ))}
    </>
  );

  return <>{isMobile ? renderAccordionMenu() : renderDesktopMenu()}</>;
}

export default React.memo(FooterNavigationClient);
